import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MetaTags from "react-meta-tags";
import Navbar from "./global-components/navbar-v2";
import CallToActionV1 from "./section-components/call-to-action-v1";
import Footer from "./global-components/footer";
import PageLoader from "./global-components/page-loader";
import { MEDIA_STORAGE_URL } from "../config/api";
import { getApartment } from "../services/api";

const ApartmentDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getApartment(id);
        setApartment(data);
      } catch (e) {
        console.error("Failed to load apartment:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div>
        <Navbar />
        <PageLoader />
        <Footer />
      </div>
    );
  }

  if (!apartment) {
    return (
      <div>
        <Navbar />
        <div
          className="container"
          style={{ padding: "80px 0", textAlign: "center" }}
        >
          <p>Apartment not found.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const displayName =
    apartment.name && typeof apartment.name === "object"
      ? apartment.name[i18n.language] || apartment.name.en || apartment.name.hr
      : apartment.name;

  const displayDescription =
    apartment.description && typeof apartment.description === "object"
      ? apartment.description[i18n.language] ||
        apartment.description.en ||
        apartment.description.hr
      : apartment.description;

  const capacity =
    apartment.additionalCapacity > 0
      ? `${apartment.mainCapacity}+${apartment.additionalCapacity}`
      : `${apartment.mainCapacity}`;

  // Sort images by order
  const sortedImages = (apartment.apartmentImages || [])
    .slice()
    .sort((a, b) => a.order - b.order);

  // Group amenities by section + roomInstance
  const sectionRoomMap = {};
  (apartment.apartmentAmenities || []).forEach((aa) => {
    const section = aa.amenity?.amenitySection;
    const sectionName = section?.name
      ? section.name[i18n.language] || section.name.en || section.name.hr
      : t("amenities");
    const amenityName = aa.amenity?.name
      ? aa.amenity.name[i18n.language] ||
        aa.amenity.name.en ||
        aa.amenity.name.hr
      : "";
    const roomInst = aa.roomInstance ?? 1;
    if (!sectionRoomMap[sectionName]) sectionRoomMap[sectionName] = {};
    if (!sectionRoomMap[sectionName][roomInst])
      sectionRoomMap[sectionName][roomInst] = [];
    const qty = aa.quantity ?? 1;
    sectionRoomMap[sectionName][roomInst].push(
      qty > 1 ? `${qty}× ${amenityName}` : amenityName,
    );
  });

  // Flatten into [label, items] pairs; number rooms when a section has > 1 instance
  const amenitySections = [];
  Object.entries(sectionRoomMap).forEach(([sectionName, roomInstances]) => {
    const entries = Object.entries(roomInstances).sort(
      ([a], [b]) => Number(a) - Number(b),
    );
    const multi = entries.length > 1;
    entries.forEach(([, items], idx) => {
      amenitySections.push([
        multi ? `${sectionName} (${idx + 1})` : sectionName,
        items,
      ]);
    });
  });

  return (
    <div className="ltn__page-ready">
      <MetaTags>
        <title>Pansion Franka - {displayName}</title>
        <meta name="description" content={displayDescription} />
        <meta property="og:title" content={`Pansion Franka - ${displayName}`} />
      </MetaTags>

      <Navbar />

      <div className="ltn__shop-details-area pb-10">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-12">
              <div className="ltn__shop-details-inner ltn__page-details-inner mb-60">
                {/* ── Header ───────────────────────────────────────────── */}
                <div className="ltn__blog-meta">
                  <ul>
                    <li className="ltn__blog-category">
                      <a className="bg-orange">{t("forRent")}</a>
                    </li>
                    <li className="ltn__blog-date">
                      <i className="far fa-calendar-alt" /> {capacity}{" "}
                      {t("persons")}
                    </li>
                  </ul>
                </div>
                <h1>{displayName}</h1>

                {/* ── Description ──────────────────────────────────────── */}
                {displayDescription && (
                  <p className="mb-30">{displayDescription}</p>
                )}

                {/* ── Amenities ────────────────────────────────────────── */}
                {amenitySections.length > 0 && (
                  <>
                    <h4 className="title-2">{t("amenities")}</h4>
                    <div className="ltn__faq-inner ltn__faq-inner-2 pb-20">
                      <div id="accordion_apt_amenities">
                        {amenitySections.map(([sectionName, items], si) => (
                          <div className="card" key={si}>
                            <h6
                              className="collapsed ltn__card-title"
                              data-bs-toggle="collapse"
                              data-bs-target={`#amenity-section-${si}`}
                              aria-expanded="false"
                            >
                              {sectionName}
                            </h6>
                            <div
                              id={`amenity-section-${si}`}
                              className="collapse"
                              data-bs-parent="#accordion_apt_amenities"
                            >
                              <div className="card-body">
                                <ul>
                                  {items.map((item, ii) => (
                                    <li key={ii}>
                                      <label className="checkbox-item">
                                        {item}
                                        <span className="checkmark" />
                                      </label>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* ── Gallery ──────────────────────────────────────────── */}
                {sortedImages.length > 0 && (
                  <>
                    <h4 className="title-2">{t("gallery")}</h4>
                    <div className="ltn__property-details-gallery mb-30">
                      <div className="row">
                        {sortedImages.map((img, idx) => {
                          const url = `${MEDIA_STORAGE_URL}/${img.image.key}`;
                          return (
                            <div className="col-md-6" key={img.id}>
                              <a href={url} data-rel="lightcase:myCollection">
                                <img
                                  className="mb-30"
                                  src={url}
                                  alt={img.image.alt || displayName}
                                />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CallToActionV1 />
      <Footer />
    </div>
  );
};

export default ApartmentDetail;
